import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { ChakraProvider } from "@chakra-ui/react";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { HelmetProvider } from "react-helmet-async";
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import ErrorBoundary from "./Components/ErrorBoundary";

import theme from "./theme";

// PostHog is initialized at module load time (before React mounts) so the singleton
// is ready when any component calls track() or posthog.capture() directly.
// Route through /ingest reverse proxy when available to avoid Safari content
// blockers that intercept requests to us.i.posthog.com. The proxy must be
// configured on CloudFront/API Gateway to forward /ingest/* → us.i.posthog.com/*.
const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
const isValidKey = posthogKey &&
                   posthogKey !== 'phc_placeholder' &&
                   posthogKey.startsWith('phc_');

if (isValidKey) {
  posthog.init(posthogKey, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || '/ingest',
    ui_host: 'https://us.posthog.com',
    capture_pageview: true,
    session_recording: {},
    loaded: (posthog) => {
      const toolbarToken = new URLSearchParams(window.location.hash.substring(1)).get('__posthog');
      if (toolbarToken) {
        posthog.loadToolbar(JSON.parse(toolbarToken));
      }
    },
  });
  window.posthog = posthog;
} else {
  console.warn(
    '[PostHog] Invalid or missing API key. Analytics disabled.',
    posthogKey ? `(key: ${posthogKey.substring(0, 10)}...)` : '(no key provided)'
  );
}

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || process.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error('Missing Clerk Publishable Key');
}

// Build timestamp to force new webpack bundle hash
window.__BUILD_TIMESTAMP__ = '2025-11-26T14:30:00Z';

const root = ReactDOM.createRoot(document.getElementById("root"));
// StrictMode removed: React.StrictMode double-invokes effects in dev mode,
// which destroys the r3f fiber tree inside EcctrlJoystick's lightweight Canvas.
// The main Canvas recovers (continuous useFrame loop) but the joystick doesn't,
// breaking mobile controls. Production builds are unaffected since double-invocation
// is dev-only, but removing StrictMode keeps dev and prod behavior consistent.
root.render(
  <PostHogProvider client={posthog}>
  <ClerkProvider
    publishableKey={clerkPubKey}
    appearance={{
      elements: {
        profileSection__activeDevices: { display: 'none' },
        profileSectionPrimaryButton__activeDevices: { display: 'none' },
      }
    }}
  >
    <HelmetProvider>
      <BrowserRouter>
        <ChakraProvider value={theme}>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </ChakraProvider>
      </BrowserRouter>
    </HelmetProvider>
  </ClerkProvider>
  </PostHogProvider>
);
